import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Bell, BellOff, CalendarDays, AlertTriangle, Loader2 } from 'lucide-react';
import { usePushNotifications } from '@/hooks/usePushNotifications';

export default function OwnerNotificationsSettings() {
    const {
        isSupported,
        isSubscribed,
        permission,
        preferences,
        loading,
        subscribing,
        error,
        subscribe,
        unsubscribe,
        updatePreferences,
    } = usePushNotifications('/owner/push');

    const [localPrefs, setLocalPrefs] = useState(preferences);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        setLocalPrefs(preferences);
    }, [preferences]);

    if (!isSupported) {
        return (
            <div className="max-w-2xl mx-auto space-y-6">
                <h1 className="text-2xl font-bold uppercase tracking-wide text-white">Notificaciones</h1>
                <Card className="bg-[#0a0a0a] border-hairline-dark">
                    <CardContent className="p-6 text-center">
                        <BellOff className="h-10 w-10 text-white/60 mx-auto mb-3" />
                        <p className="text-white/60">Tu navegador no soporta notificaciones push.</p>
                    </CardContent>
                </Card>
            </div>
        );
    }

    const handleToggle = async () => {
        if (isSubscribed) {
            await unsubscribe();
        } else {
            await subscribe();
        }
    };

    const handleSave = async () => {
        setSaving(true);
        await updatePreferences(localPrefs);
        setSaving(false);
    };

    const isToggleDisabled = loading || subscribing || permission === 'denied';

    return (
        <div className="max-w-2xl mx-auto space-y-6">
            <h1 className="text-2xl font-bold uppercase tracking-wide text-white">Notificaciones</h1>

            <Card className="bg-[#0a0a0a] border-hairline-dark">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <div className="flex items-center gap-3">
                        <div className="h-8 w-8 bg-white/10 rounded-full flex items-center justify-center">
                            <Bell className="h-4 w-4" />
                        </div>
                        <CardTitle className="text-sm font-semibold uppercase tracking-wide">
                            Notificaciones push
                        </CardTitle>
                    </div>
                    {permission !== 'default' && (
                        <Switch
                            checked={isSubscribed}
                            onCheckedChange={handleToggle}
                            disabled={isToggleDisabled}
                        />
                    )}
                </CardHeader>
                <CardContent className="pt-0 space-y-3">
                    {subscribing && (
                        <div className="flex items-center gap-2 text-sm text-white/60">
                            <Loader2 className="h-4 w-4 animate-spin" />
                            <p>{isSubscribed ? 'Desactivando notificaciones...' : 'Activando notificaciones...'}</p>
                        </div>
                    )}

                    {error && (
                        <div className="flex items-start gap-2 text-sm text-red-400">
                            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                            <p>{error}</p>
                        </div>
                    )}

                    {permission === 'denied' && (
                        <div className="flex items-start gap-2 text-sm text-amber-400">
                            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                            <p>
                                Las notificaciones están bloqueadas en este navegador.
                                Para habilitarlas, hacé clic en el ícono de candado junto a la URL,
                                buscá "Notificaciones" y cambialo a "Permitir".
                            </p>
                        </div>
                    )}

                    {permission === 'default' && (
                        <div className="space-y-3">
                            <p className="text-sm text-white/60">
                                Las notificaciones push están desactivadas. Hacé clic en el botón para permitirlas en tu navegador.
                            </p>
                            <Button
                                onClick={subscribe}
                                disabled={subscribing}
                                className="w-full"
                            >
                                {subscribing ? (
                                    <>
                                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                        Solicitando permiso...
                                    </>
                                ) : (
                                    <>
                                        <Bell className="h-4 w-4 mr-2" />
                                        Activar notificaciones
                                    </>
                                )}
                            </Button>
                        </div>
                    )}

                    {permission === 'granted' && !isSubscribed && !error && !subscribing && (
                        <p className="text-sm text-white/60">
                            Hacé clic en el switch para suscribirte a notificaciones push.
                        </p>
                    )}

                    {isSubscribed && !subscribing && (
                        <p className="text-sm text-white/60">
                            Las notificaciones push están habilitadas.
                        </p>
                    )}
                </CardContent>
            </Card>

            {isSubscribed && (
                <Card className="bg-[#0a0a0a] border-hairline-dark">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-semibold uppercase tracking-wide">
                            Preferencias
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-6">
                        {/* Class schedule reminder */}
                        <div className="flex items-start justify-between gap-4">
                            <div className="flex items-start gap-3">
                                <CalendarDays className="h-5 w-5 text-white/60 mt-0.5" />
                                <div>
                                    <Label className="text-white font-medium">Recordatorio de clases</Label>
                                    <p className="text-xs text-white/60">
                                        Te avisamos si tu gimnasio no tiene clases programadas para que puedas organizarlas.
                                    </p>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <Switch
                                    checked={localPrefs.class_schedule_reminder}
                                    onCheckedChange={(v) => setLocalPrefs((prev) => ({ ...prev, class_schedule_reminder: v }))}
                                />
                                <Label className="text-xs text-white/60">Habilitado</Label>
                            </div>
                        </div>

                        <Button
                            onClick={handleSave}
                            disabled={saving}
                            className="w-full"
                        >
                            {saving ? 'Guardando...' : 'Guardar preferencias'}
                        </Button>
                    </CardContent>
                </Card>
            )}
        </div>
    );
}
